import {
  AclType,
  InboundState,
  InboundStatus,
  OutboundStatus,
  UNSPOTTED_TRACK_ID,
} from "../../../../utils/constants";
import { isAuthorized } from "../../../../utils/appUtils";
import { getAisleFullName } from "../../../../utils/locationUtils";
import { getTrackCarDisplayFields } from "../../../../utils/trackListUtils";
import { InboundIcon, OutboundIcon } from "../../../../components/common/iconography";

/**
 * Get CSS class name for a shipment based on its state
 * @param {Object} shipmentItem - Shipment item
 * @param {Array} selectedShipmentIds - Array of selected shipment IDs
 * @param {string} accountFilterId - Currently selected account filter ID
 * @param {string} defaultShipmentId - Default shipment ID
 * @param {Object} currentShipment - Current shipment
 * @param {Object} selectedShipment - Selected shipment
 * @param {Object} classes - Material-UI classes object
 * @returns {string} CSS class name
 */
export const getShipmentClassName = (
  shipmentItem,
  selectedShipmentIds,
  accountFilterId,
  defaultShipmentId,
  currentShipment,
  selectedShipment,
  classes,
) => {
  const inbound = shipmentItem?.inbound;
  const outbound = shipmentItem?.outbound;
  const status = inbound?.status ?? outbound?.status;
  const state = inbound?.state ?? outbound?.state;
  if (selectedShipmentIds.includes(shipmentItem.id))
    return classes.trackLocationSelected;
  if (!!accountFilterId && shipmentItem.accountId === accountFilterId)
    return classes.trackLocationAccountMatched;
  if (
    shipmentItem.id === defaultShipmentId ||
    shipmentItem.id === currentShipment?.id ||
    shipmentItem.id === selectedShipment?.id
  )
    return classes.trackLocationCurrent;
  if (status === OutboundStatus.RELEASED) return classes.trackLocationReleased;
  if (state === InboundState.COMPLETED) return classes.trackLocationCompleted;
  return classes.trackLocation;
};

/**
 * Get display name for a track. Single source for Crew and header labels.
 * @param {Object} track - Track object
 * @param {string} [unspottedLabel='Unspotted'] - Label for the virtual unspotted lane
 * @returns {string} Display name
 */
export const getTrackDisplayName = (track, unspottedLabel = "Unspotted") => {
  if (!track) return "";
  if (track.id === UNSPOTTED_TRACK_ID) return unspottedLabel;
  return getAisleFullName(track);
};

/**
 * Get header label for a track
 * @param {Object} track - Track object
 * @returns {string} Header label
 */
export const getTrackHeaderLabel = (track) => {
  const displayName = getTrackDisplayName(track);
  if (!displayName) return "Track head";
  if (track?.id === UNSPOTTED_TRACK_ID) return "Unspotted";
  return `${displayName} Track Head`;
};

/**
 * Get icon component for a shipment
 * @param {Object} shipment - Shipment object
 * @param {Function} isOutbound - Function to check if shipment is outbound
 * @param {Function} getIconClassName - Function to get icon class name
 * @returns {JSX.Element} Icon component
 */
export const getShipmentIcon = (shipment, isOutbound, getIconClassName) => {
  if (isOutbound(shipment))
    return (
      <>
        <OutboundIcon className={getIconClassName(shipment)} />
        &nbsp;
      </>
    );
  return (
    <>
      <InboundIcon className={getIconClassName(shipment)} />
      &nbsp;{" "}
    </>
  );
};

/**
 * Get CSS class name for shipment icon
 * @param {Object} shipment - Shipment object
 * @param {Object} currentShipment - Current shipment
 * @param {Object} classes - Material-UI classes object
 * @returns {string} CSS class name
 */
export const getIconClassName = (shipment, currentShipment, classes) => {
  const inbound = shipment?.inbound;
  const outbound = shipment?.outbound;
  const status = shipment?.status ?? inbound?.status ?? outbound?.status;
  const state = shipment?.state ?? inbound?.state ?? outbound?.state;
  if (shipment.id === currentShipment?.id) return classes.whiteIcon;
  if (status === InboundStatus.RELEASED) return classes.redIcon;
  if (state === InboundState.COMPLETED) return classes.greenIcon;
  return classes.greyIcon;
};

/**
 * Get status and state from a TrackCar (canonical source: trackCar.inbound ?? trackCar.outbound).
 * @param {Object} trackCar - TrackCar record
 * @returns {{ status: string|null, state: string|null, sit: boolean|null }}
 */
export const getShipmentStatusFromTrackCar = (trackCar) => {
  if (!trackCar) return { status: null, state: null, sit: null };
  const shipment = trackCar.inbound ?? trackCar.outbound;
  return {
    status: shipment?.status ?? null,
    state: shipment?.state ?? null,
    sit: shipment?.sit ?? null,
  };
};

/**
 * Get railCarNumber from TrackCar (canonical: trackCar.railCarNumber).
 * @param {Object} trackCar - TrackCar record
 * @returns {string}
 */
export const getRailCarNumberFromTrackCar = (trackCar) =>
  trackCar?.railCarNumber ?? "";

export const haveRailcarDisplayFieldsChanged = (previousTrackCar, nextTrackCar) => {
  const previousFields = getTrackCarDisplayFields(previousTrackCar);
  const nextFields = getTrackCarDisplayFields(nextTrackCar);

  return (
    previousFields.accountId !== nextFields.accountId ||
    previousFields.accountName !== nextFields.accountName ||
    previousFields.productId !== nextFields.productId ||
    previousFields.productName !== nextFields.productName ||
    previousFields.productColor !== nextFields.productColor
  );
};

/**
 * Check if track operations are disabled
 * @returns {boolean} True if disabled
 */
export const isTrackDisabled = () => {
  if (
    !isAuthorized(AclType.TRACK_MANAGEMENT) ||
    !isAuthorized(AclType.ASSIGN_LOCATION)
  )
    return true;
  return false;
};
